/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { CircleAlert, CircleCheck, TriangleAlert, type LucideIcon } from 'lucide-react';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

/* Import components */
import OverviewAggregatedCard from '@/components/tabs/overview/metrics/cards/OverviewAggregatedCard';

/* Import types */
import type { OverviewStatusCardData } from '@/lib/overview/overviewSystemAggregation';

interface OverviewStatusCardProps {
    card: OverviewStatusCardData;
    showKpis: boolean;
}

const STATUS_CARD_CONFIG: Record<OverviewStatusCardData['statusCategory'], {
    icon: LucideIcon;
    label: string;
}> = {
    danger: {
        icon: CircleAlert,
        label: 'Error',
    },
    success: {
        icon: CircleCheck,
        label: 'OK',
    },
    warning: {
        icon: TriangleAlert,
        label: 'Warning',
    },
};

function OverviewStatusCard({ card, showKpis }: OverviewStatusCardProps) {
    const { t } = useI18n();
    const statusConfig = STATUS_CARD_CONFIG[card.statusCategory];
    const statusLabel = t(statusConfig.label);
    const description = t('Metrics with {status} status.', { status: statusLabel });

    return (
        <OverviewAggregatedCard
            ariaLabel={description}
            card={card}
            description={description}
            icon={statusConfig.icon}
            showKpis={showKpis}
            title={statusLabel}
            to={`${APP_ROUTES.statuses}/${card.statusCategory}`}
        />
    );
}

export default OverviewStatusCard;
